import React, { useState } from 'react';
import { Gift, CheckCircle2, Sparkles, LogIn, Lock, Copy, Check } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { registerClubMember } from '../../../hooks/useStoreApi';
import { clubBenefitHeadline, clubBenefitDescription } from '../../../utils/clubBenefit';

/**
 * Customers-club registration modal.
 *
 * The club is only available to registered (logged-in) users:
 *   - Guests see a "login required" prompt with a redirect-to-login button.
 *   - Logged-in users join in a single click — their details already exist on
 *     their account, so no form is shown.
 */
export function ClubRegisterModal({ isOpen, onClose, storeSlug, storeName, benefit, isGuest, user, onLogin }) {
    const offerHeadline = clubBenefitHeadline(benefit);
    const offerDescription = clubBenefitDescription(benefit);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    const [referral, setReferral] = useState(null);
    const [copied, setCopied] = useState(false);

    const reset = () => {
        setError(null);
        setDone(false);
        setSubmitting(false);
        setReferral(null);
        setCopied(false);
    };

    const copyCode = async () => {
        if (!referral?.code) return;
        try {
            await navigator.clipboard.writeText(referral.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard unavailable */ }
    };

    const handleClose = () => {
        reset();
        onClose?.();
    };

    const handleJoin = async () => {
        if (submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await registerClubMember(storeSlug, {
                userId: user?.id,
                name: user?.name,
                email: user?.email,
            });
            setReferral(res?.referral || null);
            setDone(true);
        } catch (err) {
            setError(err.message || 'ההרשמה נכשלה, נסו שוב');
        } finally {
            setSubmitting(false);
        }
    };

    const BenefitPreview = () => (
        (offerHeadline || offerDescription) ? (
            <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-4 flex gap-3">
                <Gift className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-brand-700 mb-1 flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5" /> ההטבה שלך
                    </p>
                    {offerHeadline && <p className="text-sm font-bold text-ink">{offerHeadline}</p>}
                    {offerDescription && offerDescription !== offerHeadline && (
                        <p className="text-sm text-ink-muted whitespace-pre-line mt-0.5">{offerDescription}</p>
                    )}
                </div>
            </div>
        ) : null
    );

    // --- Guest: must log in ---------------------------------------------
    if (isGuest) {
        return (
            <Modal isOpen={isOpen} onClose={handleClose} size="sm" title="נדרשת התחברות">
                <div className="space-y-4">
                    <div className="text-center space-y-2 py-1">
                        <div className="mx-auto w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center">
                            <Lock className="w-7 h-7 text-brand-600" />
                        </div>
                        <p className="text-ink font-semibold">מועדון הלקוחות זמין למשתמשים רשומים</p>
                        <p className="text-sm text-ink-muted">
                            התחברו לחשבון כדי להצטרף למועדון של {storeName || 'החנות'} וליהנות מההטבות.
                        </p>
                    </div>
                    <BenefitPreview />
                    <Button variant="primary" fullWidth size="lg" icon={<LogIn />} onClick={onLogin}>
                        התחברות
                    </Button>
                    <Button variant="ghost" fullWidth onClick={handleClose}>אולי מאוחר יותר</Button>
                </div>
            </Modal>
        );
    }

    // --- Logged-in: one-click join --------------------------------------
    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size="sm"
            title={done ? 'נרשמת למועדון!' : 'מועדון הלקוחות'}
            subtitle={done ? undefined : storeName}
        >
            {done ? (
                <div className="space-y-4 py-1">
                    <div className="text-center space-y-2">
                        <div className="mx-auto w-14 h-14 rounded-full bg-success-50 flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-success-600" />
                        </div>
                        <p className="text-ink font-semibold">תודה שהצטרפת! 🎉</p>
                    </div>

                    {referral ? (
                        <>
                            <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-4 text-center space-y-2">
                                <p className="text-xs font-semibold text-brand-700">הקוד האישי שלך לשיתוף</p>
                                <div className="flex items-center justify-center gap-2">
                                    <span className="font-mono text-2xl font-extrabold tracking-widest text-ink" dir="ltr">{referral.code}</span>
                                    <button type="button" onClick={copyCode} aria-label="העתק קוד"
                                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-line hover:bg-surface-muted text-ink-muted">
                                        {copied ? <Check className="w-5 h-5 text-success-600" /> : <Copy className="w-5 h-5" />}
                                    </button>
                                </div>
                                <p className="text-sm text-ink-muted">
                                    שתפו עם עד {referral.max_uses} חברים. כל חבר שיממש מקבל {referral.friend_discount_percent}% הנחה,
                                    ואתם צוברים {referral.per_referral_percent}% על כל מימוש (עד {referral.max_credit_percent}%).
                                </p>
                            </div>

                            <div className="rounded-xl border border-line bg-surface-muted/40 p-3 flex items-center justify-between gap-2">
                                <span className="text-sm text-ink-muted">ההנחה הצבורה שלך</span>
                                <span className="text-sm font-bold text-ink">
                                    {referral.credit_percent > 0
                                        ? `${referral.credit_percent}% — הזינו את הקוד בתשלום`
                                        : 'עדיין 0% — שתפו כדי לצבור'}
                                </span>
                            </div>
                            <p className="text-xs text-ink-subtle text-center">
                                {referral.uses}/{referral.max_uses} חברים מימשו את הקוד
                            </p>

                            <Button variant="primary" fullWidth onClick={handleClose}>סגירה</Button>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-ink-muted text-center">
                                נעדכן אותך במבצעים והטבות מיוחדות ללקוחות החוזרים.
                            </p>
                            <Button variant="primary" fullWidth onClick={handleClose}>סגירה</Button>
                        </>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    <BenefitPreview />
                    <p className="text-sm text-ink-muted">
                        {user?.name ? `שלום ${user.name}, ` : ''}הצטרפו למועדון הלקוחות בלחיצה אחת — הפרטים שלך כבר אצלנו.
                    </p>
                    {error && (
                        <p className="text-sm text-danger-700 bg-danger-50 border border-danger-200 rounded-lg px-3 py-2">
                            {error}
                        </p>
                    )}
                    <Button
                        type="button"
                        variant="primary"
                        fullWidth
                        size="lg"
                        icon={<Gift />}
                        loading={submitting}
                        disabled={submitting}
                        onClick={handleJoin}
                    >
                        {submitting ? 'מצטרף…' : 'הצטרפות למועדון'}
                    </Button>
                </div>
            )}
        </Modal>
    );
}

export default ClubRegisterModal;
